import React from 'react';
import { useTextGeneration } from '@/hooks/text-generation/useTextGeneration';
import { useQuery } from '@tanstack/react-query';
import { PenTool, Sparkles, User, FileText } from 'lucide-react';
import { StandardSelect, SelectOption } from '@/components/ui/form-fields/StandardSelect';

interface WritingStyleDropdownProps {
  selectedAssistantId?: string;
  onAssistantChange: (assistantId: string) => void;
}

const WritingStyleDropdown: React.FC<WritingStyleDropdownProps> = ({
  selectedAssistantId,
  onAssistantChange
}) => {
  const { getAssistants } = useTextGeneration();

  const { data: assistants = [], isLoading } = useQuery({
    queryKey: ['text-assistants'],
    queryFn: getAssistants,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Only include assistants that are marked as writing styles and not "Default Assistant"
  const writingStyleAssistants = assistants.filter(
    (assistant: any) =>
      assistant.is_writing_style === true && assistant.id !== 'default-assistant'
  );

  // Map assistant names to appropriate icons
  const getIconForAssistant = (name: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('creative') || nameLower.includes('story')) return Sparkles;
    if (nameLower.includes('professional') || nameLower.includes('business')) return FileText;
    if (nameLower.includes('personal') || nameLower.includes('casual')) return User;
    return PenTool; // default icon
  };

  // Convert assistants to SelectOption format
  const assistantOptions: SelectOption[] = [
    { value: '', label: 'None' }, // Add None option
    ...writingStyleAssistants.map((assistant: any) => ({
      value: assistant.id,
      label: assistant.name
    }))
  ];

  // Handle selection change
  const handleChange = (value: string | string[]) => {
    const selectedValue = Array.isArray(value) ? value[0] || '' : value;
    onAssistantChange(selectedValue);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg h-10" />
    );
  }

  return (
    <StandardSelect
      id="writing-style"
      label="Writing Style"
      icon={<PenTool size={16} />}
      colorVariant="purple"
      value={selectedAssistantId || ''}
      onChange={handleChange}
      options={assistantOptions}
      placeholder="Select a writing style"
    />
  );
};

export default WritingStyleDropdown;
